import React from 'react';
import '@jwp/ott-common/src/modules/register';
import { container } from '@jwp/ott-common/src/modules/container';
import { container as uiComponentContainer } from '@jwp/ott-ui-react/src/modules/container';
import StorageService from '@jwp/ott-common/src/services/StorageService';
import { BROADCAST_CHANNEL, GET_CUSTOMER_IP, REGISTER_WITH_CONFIG } from '@jwp/ott-common/src/modules/types';
import type { GetCustomerIP } from '@jwp/ott-common/types/get-customer-ip';
import LogTransporter from '@jwp/ott-common/src/services/logging/LogTransporter';
import ConsoleTransporter from '@jwp/ott-common/src/services/logging/ConsoleTransporter';
import { LogLevel } from '@jwp/ott-common/src/services/logging/LogLevel';
import SettingsService from '@jwp/ott-common/src/services/SettingsService';
import NoIniSettingsService from '@jwp/ott-common/src/services/NoIniSettingsService';
import { BroadcastChannel } from 'broadcast-channel';
import ConfigService from '@jwp/ott-common/src/services/ConfigService';
import VDConfigService from '@jwp/ott-common/src/services/integrations/vd/VDConfigService';
import { PlayerIdentifier, type PlayerProps } from '@jwp/ott-ui-react/src/components/Player/Player';
import VDContentApiService from '@jwp/ott-common/src/services/integrations/vd/VDContentApiService';
import ContentApiService from '@jwp/ott-common/src/services/ContentApiService';
import { ImageIdentifier, type ImageProps } from '@jwp/ott-ui-react/src/components/Image/Image';
import ImageBasic from '@jwp/ott-ui-react/src/components/Image/ImageBasic';
import type { Config } from '@jwp/ott-common/types/config';
import env, { configureEnv } from '@jwp/ott-common/src/env';
import StreamIQConfigService from '@jwp/ott-common/src/services/integrations/streamiq/StreamIQConfigService';
import StreamIQContentApiService from '@jwp/ott-common/src/services/integrations/streamiq/StreamIQContentApiService';

import { getOverrideIP } from '#src/utils/ip';
import { LocalStorageService } from '#src/services/LocalStorageService';
import SentryLogTransporter from '#src/services/SentryLogTransporter';

/**
 * Custom integration override
 *
 * @example
 * ```ts
 * // Custom integration services
 * const MY_INTEGRATION_NAME = 'CUSTOM';
 *
 * container.bind(AccountService).to(CustomAccountService).whenTargetNamed(MY_INTEGRATION_NAME);
 * container.bind(CheckoutService).to(CustomCheckoutService).whenTargetNamed(MY_INTEGRATION_NAME);
 * container.bind(SubscriptionService).to(CustomSubscriptionService).whenTargetNamed(MY_INTEGRATION_NAME);
 *
 * // Override integration type calculation
 * container.bind(DETERMINE_INTEGRATION_TYPE).toConstantValue((config: Config) => {
 *   return config.custom?.['custom'] ? MY_INTEGRATION_NAME : null;
 * })
 * ```
 */
export const register = async () => {
  container.bind(StorageService).to(LocalStorageService);

  container.bind(BROADCAST_CHANNEL).toConstantValue(new BroadcastChannel('jwp-refresh-token-channel'));

  // Currently, this is only used for e2e testing to override the customer ip from a browser cookie
  container.bind<GetCustomerIP>(GET_CUSTOMER_IP).toConstantValue(async () => getOverrideIP());

  /**
   * Log transporters
   *
   * Add custom log transporters by registering more modules to the LogTransporter type. The custom transporter must
   * extend the LogTransporter class.
   *
   * @example
   * ```ts
   * container.bind<LogTransporter>(LogTransporter).toDynamicValue(() => new GoogleGTMTransporter(import.meta.env.DEV ? LogLevel.SILENT : LogLevel.WARN));
   * ```
   */
  container.bind<LogTransporter>(LogTransporter).toDynamicValue(() => new ConsoleTransporter(import.meta.env.DEV ? LogLevel.DEBUG : LogLevel.ERROR));

  if (import.meta.env.APP_SENTRY_DSN) {
    container.bind<LogTransporter>(LogTransporter).toDynamicValue(() => new SentryLogTransporter());
  }

  /**
   * UI Component override
   *
   * @example
   * ```ts
   * // Define an identifier from the component (from `ui-react` for example):
   * export const CardIdentifier = Symbol('CARD');
   *
   * // Make sure the component is wrapped in the HOC:
   * export default createInjectableComponent(CardIdentifier, Card);
   *
   * // Override the component into the associated container, from register.ts:
   * import { container as uiComponentContainer } from '@jwp/ott-ui-react/src/modules/container';
   *
   * uiComponentContainer.bind<React.FC<CardProps>>(CardIdentifier).toConstantValue(CustomCard);
   * ```
   */

  // Override ui-react component
  // uiComponentContainer.bind<React.FC<CardProps>>(CardIdentifier).toConstantValue(CustomCard);

  // Override ui-react style
  // styleContainer.bind(CardIdentifier).toConstantValue(CardStyle);

  /**
   * VD Private code below
   */
  container.rebindSync(SettingsService).to(NoIniSettingsService);

  // VD Api Services
  if (import.meta.env.APP_CONFIG_PROVIDER === 'vd') {
    container.rebindSync(ConfigService).to(VDConfigService);
  }
  if (import.meta.env.APP_CONTENT_PROVIDER === 'vd') {
    container.rebindSync(ContentApiService).to(VDContentApiService);
    if (!uiComponentContainer.isBound(ImageIdentifier)) {
      uiComponentContainer.bind<React.FC<ImageProps>>(ImageIdentifier).toConstantValue(ImageBasic);
    }
  }

  // Demo mode only: Runtime switching of ContentService and content/account base url's
  if ((__mode__ === 'demo' || __mode__ === 'dev') && import.meta.env.APP_CONFIG_PROVIDER === 'vd') {
    container.bind(REGISTER_WITH_CONFIG).toConstantValue(async ({ custom }: Config) => {
      if (custom?.contentProvider !== 'vd') {
        container.rebindSync(ContentApiService).to(ContentApiService);
        uiComponentContainer.unbind(ImageIdentifier);
        configureEnv({ APP_CONTENT_API_BASE_URL: env.APP_API_BASE_URL });
      }
      if (custom?.accountProvider !== 'vd') {
        configureEnv({ APP_ACCOUNT_API_BASE_URL: env.APP_API_BASE_URL });
      }
    });
  }

  if (import.meta.env.APP_CONFIG_PROVIDER === 'streamiq') {
    container.rebindSync(ConfigService).to(StreamIQConfigService);
    container.rebindSync(ContentApiService).to(StreamIQContentApiService);

    uiComponentContainer.rebindSync<React.FC<ImageProps>>(ImageIdentifier).toConstantValue(ImageBasic);
  }

  if (import.meta.env.APP_PLAYER === 'shaka') {
    const PlayerShaka = (await import('@jwp/ott-ui-react/src/components/PlayerShaka/PlayerShaka')).default;

    uiComponentContainer.bind<React.FC<PlayerProps>>(PlayerIdentifier).toConstantValue(PlayerShaka);
  }
};
